import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSSE } from '../app/providers/SseProvider';
import { useExecutionsStore } from '../store/executionsStore';
import type { Signal } from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';
import { SMOKE_TEST_STATUS_CHANGED_SSE_EVENT } from '@terra/shared-contracts-smoke-test-history';

interface SignalsQueryData {
  signals: Signal[];
}

const ExecutionCacheItemSchema = z.looseObject({
  signalId: z.string().optional(),
  signal: z
    .object({
      id: z.string().optional(),
    })
    .optional(),
});

/**
 * Hook that subscribes to real-time smoke test status changes via SSE
 * and updates the React Query cache to reflect the current running state
 */
export const useSmokeTestStatusSSE = ({
  applicationId,
}: {
  applicationId?: string;
}) => {
  const { subscribe } = useSSE();
  const queryClient = useQueryClient();
  const { addRunningExecution, removeRunningExecution } = useExecutionsStore();

  useEffect(() => {
    if (!applicationId) return;

    const unsubscribe = subscribe(
      SMOKE_TEST_STATUS_CHANGED_SSE_EVENT,
      (event) => {
        const { signalId, applicationId: eventAppId, status } = event;

        // Only process events for the current application
        if (eventAppId !== applicationId) {
          return;
        }

        // Update the signals query cache
        queryClient.setQueriesData(
          { queryKey: ['endpoint-signals-paginated'], exact: false },
          (oldData: SignalsQueryData | undefined) => {
            if (!oldData || !oldData.signals) return oldData;

            const updatedSignals = oldData.signals.map((signal) => {
              if (signal.id === signalId) {
                return {
                  ...signal,
                  lastSmokeTestedAt:
                    status === 'COMPLETED'
                      ? new Date().toISOString()
                      : signal.lastSmokeTestedAt,
                };
              }
              return signal;
            });

            return {
              ...oldData,
              signals: updatedSignals,
            };
          },
        );

        // Update running executions store
        if (status === 'STARTED') {
          addRunningExecution(signalId);
        } else if (['COMPLETED', 'FAILED', 'STOPPED'].includes(status)) {
          removeRunningExecution(signalId);
        }

        // Update the executions query cache for all execution queries
        queryClient.setQueriesData(
          { queryKey: ['executions'], exact: false },
          (
            oldData: { executions: unknown[]; totalCount: number } | undefined,
          ) => {
            if (!oldData) return oldData;

            if (status === 'STARTED') {
              const placeholderExecution = {
                id: event.executionId || `temp-${signalId}`,
                signalId,
                signal: { id: signalId },
                status: 'RUNNING',
                type: 'SMOKE',
              };

              return {
                executions: [...oldData.executions, placeholderExecution],
                totalCount: oldData.totalCount + 1,
              };
            } else if (['COMPLETED', 'FAILED', 'STOPPED'].includes(status)) {
              // Remove the execution from running list
              const filteredExecutions = oldData.executions.filter(
                (execution: unknown) => {
                  const parseResult =
                    ExecutionCacheItemSchema.safeParse(execution);
                  if (!parseResult.success) {
                    return true;
                  }

                  const parsedExecution = parseResult.data;
                  return (
                    parsedExecution.signalId !== signalId &&
                    parsedExecution.signal?.id !== signalId
                  );
                },
              );

              return {
                executions: filteredExecutions,
                totalCount: Math.max(0, oldData.totalCount - 1),
              };
            }

            return oldData;
          },
        );
      },
    );

    return unsubscribe;
  }, [
    applicationId,
    subscribe,
    queryClient,
    addRunningExecution,
    removeRunningExecution,
  ]);
};
